import { useEffect, useMemo, useState } from "react";
import api, { imgSrc } from "@/lib/api";
import { Card } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Attachment } from "@/components/Attachment";
import { formatINR } from "@/lib/format";
import { Check, Package, Search, Plus, Minus, Zap, Power, ToggleLeft, Lightbulb, Square, Repeat, Landmark, LayoutGrid, Download, Boxes, Ruler, X } from "lucide-react";

// Area calculator for stone/tile sold by Sq Ft: Length(ft) x Width(ft) x Pieces -> sq ft.
export function AreaCalc({ onApply, testid }) {
  const [open, setOpen] = useState(false);
  const [l, setL] = useState("");
  const [w, setW] = useState("");
  const [n, setN] = useState("1");
  const sqft = +(((parseFloat(l) || 0) * (parseFloat(w) || 0) * (parseInt(n) || 1)).toFixed(2));
  const cell = "w-full border border-[#E6E8F0] rounded outline-none focus:border-[#101D51] h-11 px-2 text-base sm:h-8 sm:px-1.5 sm:text-sm";
  return (
    <div className="relative shrink-0">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
        className="h-9 px-2 rounded-lg border border-[#101D51]/30 text-[#101D51] flex items-center gap-1 text-xs font-medium hover:bg-[#101D51]/5">
        <Ruler className="w-4 h-4" /> sq ft
      </button>
      {open && (
        <>
          <div className="sm:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} data-testid={`${testid}-backdrop`} />
          <div
            className="z-50 bg-white border border-[#E6E8F0] shadow-2xl
                       fixed inset-x-0 bottom-0 w-full rounded-t-2xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-3 animate-slide-up
                       sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:mt-1 sm:w-60 sm:rounded-xl sm:p-3 sm:pb-3 sm:space-y-2 sm:animate-none"
            data-testid={`${testid}-pop`}>
            <div className="sm:hidden -mt-1 flex justify-center"><span className="h-1 w-10 rounded-full bg-[#E6E8F0]" /></div>
            <div className="flex items-center justify-between sm:hidden">
              <div className="text-base font-semibold text-[#101D51]">Area calculator</div>
              <button type="button" className="p-1 -mr-1 text-[#6B7189]" onClick={() => setOpen(false)} aria-label="Close" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs sm:text-[10px] text-[#6B7189]">Length ft</label><input type="number" min="0" step="any" inputMode="decimal" value={l} onChange={(e) => setL(e.target.value)} className={cell} data-testid={`${testid}-l`} /></div>
              <div><label className="text-xs sm:text-[10px] text-[#6B7189]">Width ft</label><input type="number" min="0" step="any" inputMode="decimal" value={w} onChange={(e) => setW(e.target.value)} className={cell} data-testid={`${testid}-w`} /></div>
              <div><label className="text-xs sm:text-[10px] text-[#6B7189]">Pieces</label><input type="number" min="1" step="1" inputMode="numeric" value={n} onChange={(e) => setN(e.target.value)} className={cell} data-testid={`${testid}-n`} /></div>
            </div>
            <div className="text-sm sm:text-xs text-[#101D51]">= <b data-testid={`${testid}-result`}>{sqft}</b> sq ft</div>
            <button type="button" disabled={sqft <= 0} onClick={() => { onApply(sqft); setOpen(false); }} data-testid={`${testid}-apply`}
              className="w-full h-12 sm:h-8 rounded-lg bg-[#101D51] text-white text-base sm:text-sm font-semibold sm:font-medium disabled:opacity-40">Add {sqft} sq ft</button>
          </div>
        </>)}
    </div>);
}

export const PAYMENT_TERMS = ["100% advance", "50% advance", "Against Delivery", "Net 15", "Net 30", "Net 45"];
const CAT_ICON = { Cables: Zap, Wires: Power, Switchgear: ToggleLeft, Lighting: Lightbulb, "Conduits and Fittings": Square, "Motors and Pumps": Repeat, Panels: Landmark, "Fans and Appliances": LayoutGrid, Accessories: Download, Other: Boxes };

export function F({ label, children, className = "" }) {
  return <div className={className}><label className="text-xs font-medium text-[#6B7189]">{label}</label><div className="mt-1">{children}</div></div>;
}

export function ProductThumb({ url, size = 10, className = "" }) {
  const src = imgSrc(url);
  return src
    ? <img src={src} alt="" loading="lazy" decoding="async" className={`w-${size} h-${size} rounded-md object-contain bg-white border border-[#E6E8F0] shrink-0 ${className}`} />
    : <div className={`w-${size} h-${size} rounded-md bg-[#F5F6FA] border border-[#E6E8F0] flex items-center justify-center shrink-0 ${className}`}><Package className="w-4 h-4 text-[#C7CBDA]" /></div>;
}

export function MarginBar({ cost, sell, margin, pct }) {
  const p = pct ?? (sell ? (margin / sell) * 100 : 0);
  return (
    <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-[#F5F6FA] p-3 text-sm" data-testid="qb-margin-summary">
      <div><div className="text-[11px] text-[#6B7189]">Purchase cost</div><div className="font-semibold text-[#101D51]" data-testid="qb-cost-total">{formatINR(cost)}</div></div>
      <div><div className="text-[11px] text-[#6B7189]">Selling (pre-tax)</div><div className="font-semibold text-[#101D51]" data-testid="qb-sell-total">{formatINR(sell)}</div></div>
      <div><div className="text-[11px] text-[#6B7189]">Margin</div><div className={`font-bold ${margin < 0 ? "text-[#F83A84]" : "text-[#0B7C5A]"}`} data-testid="qb-margin-total">{formatINR(margin)} <span className="text-[11px] font-medium">({p.toFixed(1)}%)</span></div></div>
    </div>);
}

// Qty stepper used on product rows and summary rows.
export function Qty({ value, onChange, unit, testid, dark = true, sm = false }) {
  const v = Number(value || 0);
  const btn = sm ? "w-8 h-8" : "w-9 h-9";
  const inp = sm ? "w-10 h-8" : "w-14 h-9";
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid={testid}>
      <div className={`flex items-center rounded-lg overflow-hidden border ${dark ? "border-[#101D51]" : "border-[#E6E8F0]"}`}>
        <button type="button" onClick={() => onChange(Math.max(0, v - 1))} className={`${btn} flex items-center justify-center ${dark ? "bg-[#101D51] text-white" : "text-[#101D51]"}`} data-testid={`${testid}-minus`}><Minus className="w-4 h-4" /></button>
        <input type="number" min="0" step="any" value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()}
          className={`${inp} text-center text-sm font-semibold text-[#101D51] outline-none`} data-testid={`${testid}-input`} />
        <button type="button" onClick={() => onChange(v + 1)} className={`${btn} flex items-center justify-center ${dark ? "bg-[#101D51] text-white" : "text-[#101D51]"}`} data-testid={`${testid}-plus`}><Plus className="w-4 h-4" /></button>
      </div>
      {unit && <span className="text-xs text-[#6B7189]">{unit}</span>}
    </div>);
}

// ---------- Step 1: price book + quote details ----------
export function Step1({ form, setForm, priceBooks, customer, setCustomer, enquiry }) {
  const activeBooks = useMemo(() => priceBooks.filter((p) => p.active), [priceBooks]);
  const C = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });
  const Fm = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h2 className="text-lg font-semibold text-[#101D51]">Price book</h2>
        <p className="text-sm text-[#6B7189] mb-3">Products and prices on the next page come from this book.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="qb-pricebooks">
          {activeBooks.map((p) => {
            const on = form.price_book_id === p.id;
            return (
              <button key={p.id} type="button" onClick={() => setForm({ ...form, price_book_id: p.id, lines: [] })} data-testid={`qb-pb-${p.id}`}
                className={`relative text-left rounded-xl border-2 p-4 min-h-[96px] transition-colors ${on ? "border-[#101D51] bg-[#101D51]/5" : "border-[#E6E8F0] bg-white hover:border-[#C7CBDA]"}`}>
                {on && <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#101D51] text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                <div className="font-semibold text-[#101D51] pr-6">{p.name}</div>
                <div className="text-xs text-[#6B7189] mt-1">{p.description || (p.is_standard ? "Full list price" : "Custom prices")}</div>
              </button>);
          })}
        </div>
        {enquiry && (
          <Card className="mt-4 p-4 bg-[#F5F6FA] border-l-4 border-l-[#101D51]" data-testid="qb-enquiry-brief">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7189] mb-1">What the customer asked for</div>
            <div className="text-sm text-[#101D51] whitespace-pre-wrap max-h-40 overflow-y-auto">{enquiry.description}</div>
            {enquiry.attachments?.length > 0 && <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mt-3">{enquiry.attachments.map((a) => <Attachment key={a.message_id} m={{ id: a.message_id, type: a.type, media: a.media }} compact gallery={enquiry.attachments} />)}</div>}
          </Card>)}
      </div>
      <div>
        <h2 className="text-lg font-semibold text-[#101D51]">Quote details</h2>
        <p className="text-sm text-[#6B7189] mb-3">{enquiry ? "Filled from the enquiry. Change anything that is wrong." : "Filled from the customer record."}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Customer name" className="sm:col-span-2"><Input value={customer.company || customer.name || ""} onChange={C(customer.company ? "company" : "name")} className="h-10" data-testid="qb-cust-name" /></F>
          <F label="Phone"><Input value={customer.phone || ""} onChange={C("phone")} className="h-10" /></F>
          <F label="Email"><Input value={customer.email || ""} onChange={C("email")} className="h-10" /></F>
          <F label="GSTIN"><Input value={customer.gstin || ""} onChange={C("gstin")} className="h-10 uppercase" data-testid="qb-cust-gstin" /></F>
          <F label="Place of supply (state)"><Input value={customer.state || ""} onChange={C("state")} className="h-10" /></F>
          <F label="Billing address" className="sm:col-span-2"><Textarea rows={2} value={customer.billing_address || ""} onChange={C("billing_address")} /></F>
          <F label="Quote date"><Input type="date" value={new Date().toISOString().slice(0, 10)} readOnly className="h-10 bg-[#F5F6FA]" /></F>
          <F label="Valid until"><Input type="date" data-testid="qb-valid-until" value={form.valid_until} onChange={Fm("valid_until")} className="h-10" /></F>
          <F label="Payment terms">
            <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
              <SelectTrigger className="h-10" data-testid="qb-payment-terms"><SelectValue /></SelectTrigger>
              <SelectContent>{[...new Set([...PAYMENT_TERMS, form.payment_terms].filter(Boolean))].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></F>
          <F label="Delivery terms"><Input value={form.delivery_terms} onChange={Fm("delivery_terms")} placeholder="Ex works, 3 days" className="h-10" /></F>
          <F label="Note to customer" className="sm:col-span-2"><Textarea rows={2} value={form.notes} onChange={Fm("notes")} /></F>
        </div>
      </div>
    </div>);
}

// ---------- Step 2: categories + products ----------
export function CategoryGrid({ meta, active, setActive, counts, testid = "qb-cats" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={testid}>
      {meta.map((c) => {
        const Icon = CAT_ICON[c.name] || Boxes;
        const n = counts[c.name] || 0;
        const on = active === c.name;
        return (
          <button key={c.name} type="button" onClick={() => setActive(c.name)} data-testid={`qb-cat-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
            className={`relative rounded-xl border-2 p-4 flex flex-col items-center gap-1 min-h-[104px] transition-colors ${on ? "border-[#101D51] bg-[#101D51]/5" : "border-[#E6E8F0] bg-white hover:border-[#C7CBDA]"}`}>
            {n > 0 && <span className="absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#101D51] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`qb-cat-count-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>{n}</span>}
            <Icon className="w-7 h-7 text-[#101D51]" />
            <div className="text-sm font-medium text-[#101D51] text-center break-words leading-tight">{c.name}</div>
            <div className="text-[11px] text-[#6B7189]">{c.count} product{c.count === 1 ? "" : "s"}</div>
          </button>);
      })}
    </div>);
}

export function ProductList({ category, priceBookId, lines, onAdd, onQty }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  useEffect(() => {
    setItems(null);
    const t = setTimeout(() => api.get(`/quote-catalog?price_book_id=${priceBookId}&q=${encodeURIComponent(q)}&category=${encodeURIComponent(category || "")}`).then((r) => setItems(r.data)), 200);
    return () => clearTimeout(t);
  }, [category, priceBookId, q]);
  const brands = useMemo(() => {
    const m = {}; (items || []).forEach((p) => { if (p.brand) m[p.brand] = (m[p.brand] || 0) + 1; }); return Object.entries(m).sort();
  }, [items]);
  const shown = (items || []).filter((p) => !brand || p.brand === brand);
  const qtyOf = (id) => lines.find((l) => l.product_id === id)?.quantity;
  return (
    <div data-testid="qb-products">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-baseline gap-2"><h2 className="text-lg font-semibold text-[#101D51]">{category || "All products"}</h2>{items && <span className="text-xs text-[#6B7189]">Showing {shown.length} of {items.length}</span>}</div>
        <div className="relative w-full sm:w-64"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
          <Input data-testid="qb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={category ? "Search in this category" : "Search all products"} className="pl-9 h-10" /></div>
      </div>
      {brands.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-2" data-testid="qb-brands">
          <Chip on={!brand} onClick={() => setBrand("")}>All brands ({items.length})</Chip>
          {brands.map(([b, n]) => <Chip key={b} on={brand === b} onClick={() => setBrand(b)}>{b} ({n})</Chip>)}
        </div>)}
      <div className="space-y-2">
        {items === null && <div className="text-sm text-[#6B7189] py-6 text-center">Loading…</div>}
        {items && shown.length === 0 && <div className="text-sm text-[#6B7189] py-6 text-center">No products here</div>}
        {shown.map((p) => {
          const qty = qtyOf(p.id);
          const on = qty !== undefined;
          return (
            <div key={p.id} className={`rounded-xl border-2 px-2.5 py-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2.5 ${on ? "border-[#101D51] bg-[#101D51]/5" : "border-[#E6E8F0] bg-white"}`} data-testid={`qb-prod-${p.code}`}>
              {/* Line 1 on mobile (thumb + name); left cell on sm+ */}
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <ProductThumb url={p.image_url} size={9} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#101D51] truncate leading-tight">{p.name}</div>
                  <div className="text-[11px] text-[#6B7189] truncate">{p.brand ? `${p.brand} · ` : ""}{p.code}</div>
                  {p.stock_tracked && (
                    <div className={`text-[10px] font-semibold ${p.available > 0 ? "text-[#0B7C5A]" : "text-[#B91C1C]"}`} data-testid={`qb-stock-${p.code}`}>
                      {p.available > 0 ? `Available: ${p.available}` : "Out of stock"}
                    </div>)}
                </div>
              </div>
              {/* Line 2 on mobile (price + qty/add, right-aligned); right cell on sm+ */}
              <div className="flex items-center justify-end gap-2.5 shrink-0">
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#101D51] leading-tight whitespace-nowrap">{formatINR(p.book_price)}</div>
                  <div className="text-[10px] text-[#6B7189]">/ {p.unit}</div>
                </div>
                {(p.unit || "").toLowerCase().includes("sq") && (
                  <AreaCalc testid={`qb-calc-${p.code}`} onApply={(s) => { if (!on) onAdd(p); onQty(p.id, s); }} />)}
                {on
                  ? <Qty value={qty} onChange={(v) => onQty(p.id, v)} testid={`qb-qty-${p.code}`} sm />
                  : <button type="button" onClick={() => onAdd(p)} aria-label="Add" className="h-9 w-9 rounded-lg bg-[#101D51] text-white flex items-center justify-center shrink-0 active:scale-95 transition-transform" data-testid={`qb-pick-${p.code}`}><Plus className="w-5 h-5" /></button>}
              </div>
            </div>);
        })}
      </div>
    </div>);
}

function Chip({ on, children, ...p }) {
  return <button type="button" {...p} className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium border ${on ? "bg-[#101D51] text-white border-[#101D51]" : "bg-white text-[#6B7189] border-[#E6E8F0]"}`}>{children}</button>;
}
